/**
 * 再認証ダイアログ（fresh-auth の取り直し）。
 *
 * `requireFreshAuth()` が付いた操作（決済手段の登録・即時解約など）は
 *   - 2 要素目を一度も通していないセッション … `MFA_REQUIRED`
 *   - 2 要素目から 5 分以上経過                … `REAUTH_REQUIRED`
 * で 401 になる。**画面に取り直しの導線が無いと、その操作は
 * 「ログインし直す」以外の方法で永久に実行できない。**
 *
 * 2 つのエラーは出口が違うので、必ず区別して出す:
 *   - `MFA_REQUIRED`   … まだ 2 段階認証を登録していない → 登録画面へ送る
 *   - `REAUTH_REQUIRED`… 登録済みだが期限切れ           → コードを入力させる
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '@mail-sv/ui';
import { ApiError } from '../lib/api.js';
import { reauth } from '../lib/auth.js';
import { AppLink } from '../lib/nav.js';
import { FormAlert } from './form.js';

/** この操作は再認証で解けるか（＝ダイアログを出す意味があるか）を判定する。 */
export function needsReauth(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'REAUTH_REQUIRED';
}

/** 2 段階認証そのものが未登録か（登録画面へ送るしかない）。 */
export function needsMfaEnrollment(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'MFA_REQUIRED';
}

export function ReauthDialog({
  open,
  onOpenChange,
  /** 何のための再認証かを 1 行で書く（「解約の手続き」など）。 */
  purpose,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purpose: string;
  onSuccess: () => void;
}) {
  const [method, setMethod] = React.useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = React.useState('');

  const submit = useMutation({
    mutationFn: () => reauth(method, code.trim()),
    onSuccess: () => {
      setCode('');
      onOpenChange(false);
      onSuccess();
    },
  });

  React.useEffect(() => {
    if (open) {
      setCode('');
      setMethod('totp');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => (submit.isPending ? undefined : onOpenChange(next))}>
      <DialogContent className="max-w-md">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim().length >= 4) submit.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>本人確認</DialogTitle>
            <DialogDescription>
              {purpose}のため、認証アプリの確認コードを入力してください。
            </DialogDescription>
          </DialogHeader>

          <FormAlert error={submit.error} />

          <Field
            label={method === 'totp' ? '確認コード（6 桁）' : 'リカバリコード'}
            htmlFor="reauth-code"
            required
          >
            <Input
              id="reauth-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={method === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              autoFocus
            />
          </Field>

          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            onClick={() => setMethod((m) => (m === 'totp' ? 'recovery' : 'totp'))}
          >
            {method === 'totp'
              ? '認証アプリが使えない場合（リカバリコードを使う）'
              : '認証アプリの確認コードを使う'}
          </button>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submit.isPending}
            >
              キャンセル
            </Button>
            <Button type="submit" loading={submit.isPending} disabled={code.trim().length < 4}>
              確認する
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 2 段階認証が未登録のときの案内。
 * **ここでボタンを押させても解決しない**ので、登録画面への導線だけを出す。
 */
export function MfaEnrollmentNotice({ purpose }: { purpose: string }) {
  return (
    <Alert variant="warning">
      <AlertTitle>2 段階認証の登録が必要です</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          {purpose}は、お客様の資産に関わる操作のため 2 段階認証を必須にしています。
          セキュリティ設定から認証アプリを登録してください。
        </p>
        <Button asChild variant="outline" size="sm">
          <AppLink to="/security">セキュリティ設定を開く</AppLink>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
